import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'sonner';
import {FiArchive, FiMic, FiRotateCcw, FiTool} from 'react-icons/fi';
import {
  archivePersona,
  countryFlag,
  fetchAllPersonae,
  formatLocale,
  unarchivePersona,
} from './api';
import './ChatList.css';
import './PersonaList.css';

// Remembers the "show archived" choice across reloads. Archived personae are hidden
// by default, so without this a user working through their archive would have to
// re-enable the toggle on every visit.
const SHOW_ARCHIVED_KEY = 'personaList.showArchived';

function readShowArchived() {
  try {
    return window.localStorage.getItem(SHOW_ARCHIVED_KEY) === 'true';
  } catch {
    // Private browsing and similar can make localStorage throw on access.
    return false;
  }
}

function writeShowArchived(value) {
  try {
    window.localStorage.setItem(SHOW_ARCHIVED_KEY, String(value));
  } catch {
    // Losing the preference is not worth failing the toggle over.
  }
}

// Lists the personae owned by the signed-in user. Unlike the chat-creation
// dropdown (see ChatList.js) this deliberately asks for includePublic:false, so
// it shows only the user's own personae and not the shared public ones.
function PersonaList() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const [personae, setPersonae] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(readShowArchived);

  const fetchPersonae = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      const list = await fetchAllPersonae({includePublic: false, includeArchived: showArchived});
      setPersonae(list);
    } catch (err) {
      setError(t('personaList.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [t, showArchived]);

  useEffect(() => {
    fetchPersonae();
  }, [fetchPersonae]);

  const toggleShowArchived = (checked) => {
    setShowArchived(checked);
    writeShowArchived(checked);
  };

  const unarchive = useCallback(async (persona, {silent = false} = {}) => {
    try {
      await unarchivePersona(persona.id);
      await fetchPersonae();
      if (!silent) {
        toast(t('personaList.restoredToast', {name: persona.name}));
      }
    } catch (err) {
      toast.error(t('personaList.restoreFailed', {name: persona.name}));
    }
  }, [fetchPersonae, t]);

  // Unless the toggle is on, the row disappears the moment it is archived, so the
  // toast carries the only affordance to undo it.
  const archive = useCallback(async (persona) => {
    try {
      await archivePersona(persona.id);
      await fetchPersonae();
      toast(t('personaList.archivedToast', {name: persona.name}), {
        action: {
          label: t('common.undo'),
          onClick: () => unarchive(persona, {silent: true}),
        },
      });
    } catch (err) {
      toast.error(t('personaList.archiveFailed', {name: persona.name}));
    }
  }, [fetchPersonae, t, unarchive]);

  return (
    <div className="chat-list-container">
      <div className="chat-list-header">
        <h2>{t('personaList.title')}</h2>
        <button className="new-chat-button" onClick={() => navigate('/personas/build')}>
          <FiTool className="sidebar-button-icon" aria-hidden="true"/> {t('personaList.new')}
        </button>
      </div>
      <div className="chat-list-content">
        <label className="persona-list-show-archived">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(event) => toggleShowArchived(event.target.checked)}
          />
          {t('personaList.showArchived')}
        </label>
        {isLoading && <div className="chat-list-status">{t('personaList.loading')}</div>}
        {error && <div className="error-message">{error}</div>}
        {!isLoading && !error && personae.length === 0 && (
          <div className="chat-list-status">{t('personaList.empty')}</div>
        )}
        {!isLoading && !error && personae.length > 0 && (
          <ul className="chat-list">
            {personae.map((persona) => (
              <li key={persona.id} className="chat-list-item persona-list-item">
                <span className="chat-list-item-main">
                  {persona.originalLocale?.countryCode && (
                    <span
                      className="chat-list-item-flag"
                      title={formatLocale(persona.originalLocale)}
                    >
                      {countryFlag(persona.originalLocale.countryCode)}
                    </span>
                  )}
                  <span className="persona-list-item-text">
                    <span className="chat-list-item-name">
                      {persona.name}
                      {persona.archived && (
                        <span className="persona-list-item-badge">
                          {t('personaList.archived')}
                        </span>
                      )}
                    </span>
                    {persona.description && (
                      <span className="persona-list-item-description">
                        {persona.description}
                      </span>
                    )}
                  </span>
                </span>
                {/* One element however many buttons are in it, so the row keeps exactly two flex
                    children: space-between would otherwise strand the first button in the middle
                    of the row instead of leaving both at its right edge. */}
                <span className="persona-list-item-actions">
                  {persona.archived ? (
                    <button
                      type="button"
                      className="persona-list-item-action"
                      aria-label={t('personaList.restoreAction', {name: persona.name})}
                      title={t('personaList.restore')}
                      onClick={() => unarchive(persona)}
                    >
                      <FiRotateCcw aria-hidden="true"/>
                    </button>
                  ) : (
                    <>
                      {/* Speaking to a persona one has retired is not a thing anyone means to do,
                          so an archived row offers only the way back. */}
                      <button
                        type="button"
                        className="persona-list-item-action"
                        aria-label={t('personaList.speakAction', {name: persona.name})}
                        title={t('personaList.speak')}
                        onClick={() => navigate(`/voice/${persona.id}`)}
                      >
                        <FiMic aria-hidden="true"/>
                      </button>
                      <button
                        type="button"
                        className="persona-list-item-action"
                        aria-label={t('personaList.archiveAction', {name: persona.name})}
                        title={t('personaList.archive')}
                        onClick={() => archive(persona)}
                      >
                        <FiArchive aria-hidden="true"/>
                      </button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default PersonaList;
